import React, { useState, useEffect } from 'react';
import {
  FileText,
  BarChart2,
  User,
  ClipboardList,
  Zap,
} from 'lucide-react';
import axios from 'axios';

import StudentLogin from './StudentLogin';
import StatGenieReportCard from './StatGenieReportCard';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const HomePage = ({ onDataUpload, onClearData, hasData, questionsData, students, tests, onStudentLogin }) => {
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [activeTab, setActiveTab] = useState('aianalytics');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedTest, setSelectedTest] = useState('');
  
  // New state for database integration
  const [currentStudent, setCurrentStudent] = useState(null);
  const [studentTests, setStudentTests] = useState([]);
  const [filteredTests, setFilteredTests] = useState([]); // Add filtered tests state
  const [analyticsData, setAnalyticsData] = useState([]);
  const [percentileData, setPercentileData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [error, setError] = useState('');
  
  // Local students state to track backend response
  const [localStudents, setLocalStudents] = useState([]);

  const headerHeight = '72px';
  const filterBarHeight = '88px';

  // Function to filter SIMCAT tests with caching
  const filterSimcatTests = async (tests) => {
    console.log('🔍 Filtering SIMCAT tests from:', tests.length, 'total tests');
    
    // OPTIMIZATION 3: Cache SIMCAT filtering results in localStorage
    const cacheKey = `simcat_filter_${JSON.stringify(tests.map(t => t.testId).sort())}`;
    const cached = localStorage.getItem(cacheKey);
    
    if (cached) {
      try {
        const cachedData = JSON.parse(cached);
        // Check if cache is still valid (5 minutes)
        if (cachedData.expiry && cachedData.expiry > Date.now()) {
          console.log('🚀 Using cached SIMCAT filter results');
          return cachedData.results;
        }
      } catch (e) {
        // Invalid cache, remove it
        localStorage.removeItem(cacheKey);
      }
    }
    
    // Filter tests that are SIMCATs based on the testName from TestPlayer
    const simcatTests = tests.filter(test => {
      const isSimcat = test.testName && test.testName.toLowerCase().includes('simcat');
      
      if (isSimcat) {
        console.log(`✅ Found SIMCAT: ${test.testId} -> ${test.testName}`);
      } else {
        console.log(`❌ Not SIMCAT: ${test.testId} -> ${test.testName}`);
      }
      
      return isSimcat;
    });
    
    // Cache results for 5 minutes
    const cacheData = {
      results: simcatTests,
      timestamp: Date.now(),
      expiry: Date.now() + (5 * 60 * 1000) // 5 minutes
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    
    console.log(`📊 Filtered ${simcatTests.length} SIMCAT tests from ${tests.length} total tests`);
    return simcatTests;
  };

  // Handle student login from StudentLogin component with progressive enhancement
  const handleStudentLogin = async (studentData) => {
    console.log('⚡ Fast login: Setting user data immediately');
    console.log('📋 Student category:', studentData.category);
    
    // Call parent to handle navigation
    if (onStudentLogin) {
      onStudentLogin(studentData);
      return;
    }
    
    // Fallback: Set user immediately for fast UI response (old behavior)
    setCurrentStudent(studentData);
    setSelectedStudent(`student_${studentData.imsPin}`);
    setSelectedTest('');
    setAnalyticsData([]);
    
    // OPTIMIZATION 5: Use Web Workers for heavy filtering (fallback to main thread)
    try {
      const allTests = studentData.tests;
      
      // Filter in background using requestIdleCallback if available
      if (window.requestIdleCallback) {
        window.requestIdleCallback(async () => {
          const simcatTests = await filterSimcatTests(allTests);
          setStudentTests(allTests);
          setFilteredTests(simcatTests);
          console.log(`🎯 Showing ${simcatTests.length} SIMCAT tests out of ${allTests.length} total tests`);
        });
      } else {
        // Immediate processing for older browsers
        const simcatTests = await filterSimcatTests(allTests);
        setStudentTests(allTests);
        setFilteredTests(simcatTests);
        console.log(`🎯 Showing ${simcatTests.length} SIMCAT tests out of ${allTests.length} total tests`);
      }
    } catch (error) {
      console.error('Error processing tests:', error);
      // Fallback to basic processing
      setStudentTests(studentData.tests);
      setFilteredTests(studentData.tests);
    }
  };

  // Handle test selection and load analytics with progressive loading
  const handleTestSelection = async (testId) => {
    console.log('🔍 handleTestSelection called with:', { testId, currentStudent: currentStudent?.imsPin });
    
    if (!currentStudent || !testId) {
      console.log('❌ Missing currentStudent or testId, aborting');
      return;
    }
    
    // CLEAR ANY CACHED DATA
    setAnalyticsData([]);
    setPercentileData(null);
    
    // Test IDs from backend are now PostgreSQL format (no ObjectIds)
    // No conversion needed - backend returns PostgreSQL IDs directly from MyIMSAdmin
    console.log('🎯 Using testId directly (PostgreSQL format):', testId);
    
    const apiUrl = `${API_BASE_URL}/api/student/${currentStudent.imsPin}/test/${testId}/analytics`;
    console.log('📡 Making API request to:', apiUrl);
    console.log('🔄 FORCING FRESH DATA - No cache used');
    
    setLoadingAnalytics(true);
    setError('');
    
    try {
      const response = await axios.get(
        apiUrl,
        {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          },
          // Force fresh request
          params: {
            '_t': Date.now() // Cache buster
          }
        }
      );
      
      console.log('✅ API response received:', response.data);
      
      const { data, students: studentCols, tests: testNames, percentileData: responsePercentileData, metadata } = response.data;
      
      console.log('📊 Analytics data details:');
      console.log('  - Data length:', data.length);
      console.log('  - Student columns:', studentCols);
      console.log('  - Test names:', testNames);
      console.log('  - Percentile data available:', !!responsePercentileData);
      console.log('  - Metadata:', metadata);
      console.log('  - TIMING DATA SOURCE:', metadata?.timingDataSource);
      console.log('  - DATA SOURCE:', metadata?.dataSource);
      console.log('  - First 3 questions with timing:', data.slice(0, 3).map(q => ({
        question: q.question_number,
        area: q.area,
        timing: q[`student_${currentStudent.imsPin}_time`],
        response: q[`student_${currentStudent.imsPin}`]
      })));
      
      if (data.length > 0) {
        const sample = data[0];
        studentCols.forEach(student => {
          console.log(`    ${student}: ${sample[student]} (time: ${sample[student + '_time']})`);
        });
      }
      
      setAnalyticsData(data);
      setPercentileData(responsePercentileData);
      setLocalStudents(studentCols);
      
      console.log('📊 Updated local state:', {
        analyticsDataLength: data.length,
        studentsFromBackend: studentCols,
        selectedStudent: selectedStudent
      });
      
      // Show info message if test window is still active but data is available
      if (metadata?.testWindowActive && data.length > 0) {
        setError('') // Clear any previous errors
        console.log('🎆 Analytics loaded successfully - percentiles will be available when test window closes');
      } else {
        setError(''); // Clear any errors
      }
      
      // Call the original onDataUpload to maintain compatibility with existing components
      if (onDataUpload) {
        console.log('📤 Calling onDataUpload with data');
        onDataUpload(data, studentCols, testNames);
      }
      
      // Brief pause to show completion
      await new Promise(resolve => setTimeout(resolve, 300));
      
    } catch (error) {
      console.error('❌ Error loading analytics:', error);
      
      // Handle specific error responses
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 202 && data.testWindowActive) {
          setError(`Test window is still active. ${data.message || 'Analytics will be available once the test window closes.'}`);
        } else if (status === 404) {
          setError(data.message || 'Test data not found. Please try a different test.');
        } else {
          setError(data.message || 'Failed to load analytics data. Please try again.');
        }
      } else {
        setError('Failed to load analytics data. Please try again.');
      }
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    setCurrentStudent(null);
    setStudentTests([]);
    setFilteredTests([]);
    setAnalyticsData([]);
    setPercentileData(null);
    setSelectedStudent('');
    setSelectedTest('');
    if (onClearData) {
      onClearData();
    }
  }; // Dependencies are onDataUpload and onClearData.
                                  // importedRawData is a static import, stable.

  useEffect(() => {
    console.log('🔄 useEffect triggered - selectedTest:', selectedTest, 'currentStudent:', currentStudent?.imsPin);
    
    if (selectedTest && selectedTest !== '') {
      console.log('🗘 Calling handleTestSelection with:', selectedTest);
      handleTestSelection(selectedTest);
    } else {
      console.log('ℹ️ No test selected or test is empty');
    }
  }, [selectedTest, currentStudent]);

  // If no student is logged in, show login screen
  if (!currentStudent) {
    return <StudentLogin onStudentLogin={handleStudentLogin} />;
  }

  const NoDataMessageComponent = ({ featureName }) => (
    <div className="p-6 text-center text-slate-500">
      {loadingAnalytics ? (
        <p className="mb-1 text-lg animate-pulse">Loading {featureName} analytics...</p>
      ) : (
        <>
          <p className="mb-1 text-lg">No Data Available for {featureName}</p>
          <p className="text-sm">Please select a test to view analytics.</p>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </>
      )}
    </div>
  );

  const PromptForSelectionComponent = ({ featureName }) => (
    <div className="p-6 text-center text-slate-500">
      <p className="mb-1 text-lg">Please make a selection</p>
      <p className="text-sm">Use the filters above to choose a student and test to view {featureName}.</p>
    </div>
  );

  const renderTabContent = (contentElement, featureNameForNoData, featureNameForSelectionPrompt) => {
    console.log('🔍 renderTabContent called with:', {
      loadingAnalytics,
      analyticsDataLength: analyticsData?.length || 0,
      selectedTest,
      localStudentsLength: localStudents?.length || 0,
      selectedStudent,
      sampleAnalyticsData: analyticsData?.slice(0, 1),
      localStudents
    });
    
    if (loadingAnalytics) {
      return <div className="p-10 text-center text-slate-600 text-lg animate-pulse">Loading analytics data, please wait...</div>;
    }
    if (!analyticsData || analyticsData.length === 0) {
      return <NoDataMessageComponent featureName={featureNameForNoData} />;
    }
    if (!selectedTest) {
      return <PromptForSelectionComponent featureName={featureNameForSelectionPrompt} />;
    }
    return contentElement;
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-white border-b border-slate-200 py-4 px-6 sticky top-0 z-30">
        <div className="max-w-screen-2xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BarChart2 className="text-indigo-600" size={28} />
            <h1 className="text-2xl font-semibold text-slate-800">Test Analytics Dashboard</h1>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-sm">
              <span className="text-slate-600">Welcome, </span>
              <span className="font-medium text-slate-800">{currentStudent.imsPin}</span>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm bg-slate-100 hover:bg-slate-200 px-3 py-1 rounded-md transition"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {currentStudent && studentTests.length > 0 && filteredTests.length === 0 && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-4 sticky z-20" style={{ top: headerHeight }}>
          <div className="max-w-screen-2xl mx-auto text-center">
            <div className="text-yellow-800">
              📊 No SIMCAT tests found for your account. Analytics are only available for SIMCAT tests.
            </div>
            <div className="text-sm text-yellow-700 mt-1">
              You have {studentTests.length} total test(s), but none are SIMCATs.
            </div>
          </div>
        </div>
      )}

      {currentStudent && filteredTests.length > 0 && (
        <div className="bg-white border-b border-slate-200 px-6 py-4 sticky z-20" style={{ top: headerHeight }}>
          <div className="max-w-screen-2xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 items-end">
              <div>
                <label htmlFor="student-select" className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                  <User size={14} className="mr-1.5 text-slate-500" />
                  Student
                </label>
                <div className="block w-full p-2.5 border border-slate-300 rounded-md shadow-sm bg-slate-100 text-slate-600 text-sm">
                  {currentStudent.imsPin} (Logged In)
                </div>
              </div>
              <div>
                <label htmlFor="test-select" className="block text-sm font-medium text-slate-700 mb-1 flex items-center">
                  <ClipboardList size={14} className="mr-1.5 text-slate-500" />
                  SIMCAT Tests ({filteredTests.length} available)
                </label>
                <select
                  id="test-select"
                  name="test"
                  value={selectedTest}
                  onChange={(e) => setSelectedTest(e.target.value)}
                  className="block w-full p-2.5 border border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
                  disabled={loadingAnalytics}
                  aria-label="Select Test"
                >
                  <option value="">Select a SIMCAT test...</option>
                  {filteredTests.map((test) => {
                    // Use attemptDate from API or fallback to a default
                    const displayDate = test.attemptDate ? 
                      new Date(test.attemptDate).toLocaleDateString() : 
                      'Date unavailable';
                    
                    return (
                      <option key={test.testId} value={test.testId}>
                        {test.testName} ({displayDate})
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div 
        className={`bg-white border-b border-slate-200 px-6 sticky z-10`} 
        style={{ top: (currentStudent && filteredTests.length > 0) ? `calc(${headerHeight} + ${filterBarHeight})` : headerHeight }}
      >
        <nav className="max-w-screen-2xl mx-auto flex space-x-6 sm:space-x-8" aria-label="Main Tabs">
          <button
            onClick={() => setActiveTab('aianalytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-1.5 ${activeTab === 'aianalytics' ? 'border-purple-500 text-purple-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
          ><Zap size={16} />AI Analytics</button>
        </nav>
      </div>

      <main className="flex-1 max-w-screen-2xl mx-auto w-full p-6">
        {activeTab === 'aianalytics' && (
          <StatGenieReportCard
            selectedStudent={selectedStudent}
            selectedTest={selectedTest}
            questionsData={analyticsData}
            students={localStudents}
            tests={[selectedTest]}
            percentileData={percentileData}
            studentCategory={currentStudent?.category || 'General'}
          />
        )}
      </main>

      <footer className="bg-white border-t border-slate-200 py-5 px-6 mt-auto">
        <div className="max-w-screen-2xl mx-auto text-center text-xs text-slate-500">
            <p>© {new Date().getFullYear()} Test Analytics Dashboard.</p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;